import { useState, useCallback, useRef, useEffect } from 'react';
import { apiGet, apiPost } from './apiClient';
import { indexerTradeToMarketTrade } from './sidebarTradeTypes';
import type { IndexerTradeItem } from './sidebarTradeTypes';
import type { MarketTrade } from './useMarketTrades';

interface IndexerTradesResponse {
    event_id: string | null;
    market_ids: string[];
    trades: IndexerTradeItem[];
    trade_count: number;
    has_more: boolean;
}

const PAGE_SIZE = 20;

interface UseHistoricalMarketTradesOptions {
    /** Load trades by account (TraderView sidebar) */
    accountId?: string;
    /** Lower bound (the trader's first trade) — excludes nothing, keeps deep pages fast. */
    fromDate?: string | null;
}

export function useHistoricalMarketTrades({
    accountId,
    fromDate,
}: UseHistoricalMarketTradesOptions) {
    const [trades, setTrades] = useState<MarketTrade[]>([]);
    const [loading, setLoading] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [error, setError] = useState<Error | null>(null);
    const [marketNames, setMarketNames] = useState<
        Record<string, { question: string; event_slug?: string | null }>
    >({});
    const cursorRef = useRef<string | null>(null);
    const hasMoreRef = useRef(true);
    const loadingRef = useRef(false);
    const resolvedIdsRef = useRef<Set<string>>(new Set());
    const abortRef = useRef<AbortController | null>(null);

    const resolveMarketNames = useCallback(async (marketIds: string[]) => {
        const unknown = marketIds.filter(id => !resolvedIdsRef.current.has(id));
        if (unknown.length === 0) return;
        for (const id of unknown) resolvedIdsRef.current.add(id);
        try {
            const res = await apiPost<{
                markets: Record<
                    string,
                    { question: string; event_slug?: string | null }
                >;
            }>(
                '/api/market-names',
                { condition_ids: unknown },
                abortRef.current?.signal
            );
            if (res.markets && Object.keys(res.markets).length > 0) {
                setMarketNames(prev => ({ ...prev, ...res.markets }));
            }
        } catch (err) {
            // Roll back the optimistic "resolved" marker so a future call can retry.
            // Errors here (including AbortError) are never surfaced to the caller.
            for (const id of unknown) resolvedIdsRef.current.delete(id);
        }
    }, []);

    const loadPage = useCallback(async () => {
        if (
            !accountId ||
            !fromDate ||
            loadingRef.current ||
            !hasMoreRef.current
        )
            return;

        loadingRef.current = true;
        setLoading(true);
        setError(null);

        abortRef.current?.abort();
        const controller = new AbortController();
        abortRef.current = controller;

        const params = new URLSearchParams({ limit: String(PAGE_SIZE) });
        if (fromDate) params.set('from_ts', fromDate);
        // to_ts is a pagination cursor only. Never seed it from profile data —
        // activity.last_seen is an end-of-UTC-day rollup, so using it as the
        // initial ceiling hid every trade made since midnight.
        if (cursorRef.current) params.set('to_ts', cursorRef.current);

        try {
            const res = await apiGet<IndexerTradesResponse>(
                `/api/trades/account/${encodeURIComponent(accountId)}?${params}`,
                controller.signal
            );

            const mapped = res.trades.map(indexerTradeToMarketTrade);

            const uniqueIds = [...new Set(res.trades.map(t => t.market_id))];
            resolveMarketNames(uniqueIds);

            if (res.trades.length > 0) {
                const oldest = res.trades[res.trades.length - 1].timestamp;
                cursorRef.current =
                    typeof oldest === 'string'
                        ? oldest
                        : new Date(oldest).toISOString();
            }

            hasMoreRef.current = res.has_more;
            setHasMore(res.has_more);

            if (mapped.length > 0) {
                setTrades(prev => {
                    const existingIds = new Set(prev.map(t => t.id));
                    const newTrades = mapped.filter(
                        t => !existingIds.has(t.id)
                    );
                    if (newTrades.length === 0) return prev;
                    const combined = [...prev, ...newTrades];
                    combined.sort(
                        (a, b) => b.timestamp.getTime() - a.timestamp.getTime()
                    );
                    return combined;
                });
            }
        } catch (err) {
            if (err instanceof Error && err.name === 'AbortError') return;
            setError(err instanceof Error ? err : new Error(String(err)));
        } finally {
            if (!controller.signal.aborted) {
                loadingRef.current = false;
                setLoading(false);
            }
        }
    }, [accountId, fromDate, resolveMarketNames]);

    const reset = useCallback(() => {
        abortRef.current?.abort();
        setTrades([]);
        hasMoreRef.current = true;
        setHasMore(true);
        setError(null);
        setMarketNames({});
        cursorRef.current = null;
        resolvedIdsRef.current.clear();
        loadingRef.current = false;
        setLoading(false);
    }, []);

    const prevKeyRef = useRef(accountId);
    useEffect(() => {
        if (prevKeyRef.current !== accountId) {
            prevKeyRef.current = accountId;
            reset();
        }
    }, [accountId, reset]);

    useEffect(() => () => abortRef.current?.abort(), []);

    return {
        trades,
        loading,
        hasMore,
        error,
        loadPage,
        reset,
        marketNames,
        resolveMarketNames,
    };
}
